import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { Table } from "@similarweb/pro-logic-components";

import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { useCategoriesComparison } from "app/pages/industry-analysis/categories-context";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { useTranslation } from "pro-features/components/WithTranslation";

import { useKeyIndicatorsData } from "../hooks/useKeyIndicatorsData";

import { buildKeySizingComparisonTableData } from "./buildKeySizingComparisonTableData";
import { getColumnConfig } from "./getColumnConfig";

export const KeySizingIndicatorsComparisonWidget = () => {
    const t = useTranslation();
    const {
        params: { duration, comparedDuration },
    } = useCurrentStateAndParams();
    const durationData = DurationService.getDurationData(duration, comparedDuration);
    const isPeriodOverPeriod = DurationService.isPeriodOverPeriod();
    const isYearOverYear = comparedDuration === BasicDurations.LAST_TWELVE_MONTHS;
    const subtitleFilters = useSubtitleFilters("date");
    const { categories, hasSelectedCategoryWithSegments } = useCategoriesComparison();
    const { data, isLoading, isError } = useKeyIndicatorsData(categories.map((c) => c.category));

    const tableData = useMemo(
        () => buildKeySizingComparisonTableData(categories, data, isPeriodOverPeriod),
        [categories, data, isPeriodOverPeriod],
    );
    const columnConfig = useMemo(
        () =>
            getColumnConfig(
                isPeriodOverPeriod,
                isYearOverYear,
                durationData?.forWidget,
                hasSelectedCategoryWithSegments,
            ),
        [isPeriodOverPeriod, isYearOverYear, durationData, hasSelectedCategoryWithSegments],
    );

    return (
        <WidgetContainer
            withBorder
            minHeight={200}
            noData={isError}
            subtitleFilters={subtitleFilters}
            title={t("industryoverview.key.indicators.header.title")}
            automationId="industry-analysis-overview-comparison-key-sizing-widget"
        >
            <Table
                tableLayout="auto"
                sticky={false}
                theme="white"
                bordersConfig={{ header: false }}
                isLoading={isLoading}
                loaderRowCount={3}
                data={tableData}
                columnConfig={columnConfig}
            />
        </WidgetContainer>
    );
};
